import CCButton from '@/Atoms/CCButton';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import Spacer from '@/Atoms/Spacer';
import useUploadSupportApi from '@/Hooks/GetSupportModalApi/useUploadSupportApi';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {Ionicons} from '@expo/vector-icons';
import React, {useEffect, useMemo, useState} from 'react';
import {View} from 'react-native';
import styles from '../../Components/UserAssistance/component/SupportModal/styles';
import {ModalContent} from '@/Components/UserAssistance/component/SupportModal';
import useGetIssueCategories, {
  IssueCategory,
} from '@/Hooks/GetSupportModalApi/useGetIssueCategories';
import {useNavigation} from '@react-navigation/native';
import {formatDateToDDMMYYYY, formatTimeHHMM} from '@/Utils/CommonUtils';
import {ScreenName} from '@/navigators/StackConstants';

const ReportBug = () => {
  const navigation = useNavigation();
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [bugType, setBugType] = useState<any>(null);
  const [communicationType, setCommunicationType] = useState('');
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [issueCategories, setIssueCategories] = useState<IssueCategory[]>([]);
  const [ticketId, setTicketId] = useState<number | null>(null);
  const [commDate, setCommDate] = useState<string | null>(null);

  const {mutate: submitSupportTicket} = useUploadSupportApi(
    data => {
      setTicketId(data?.data?.issue_id || null);
      setCommDate(data?.data?.comm_sla || null);
      setSuccess(true);
    },
    error => {
      setErrorMessage('Failed to submit support ticket');
    },
  );

  const {mutate: getIssueCategories, isLoading} = useGetIssueCategories(
    data => {
      setIssueCategories(data?.data || []);
    },
    error => {
      console.error('Failed to fetch issue categories:', error);
    },
  );

  useEffect(() => {
    getIssueCategories();
  }, []);

  const isImage = useMemo(() => file && file.type.startsWith('image'), [file]);

  const handleDrop = (acceptedFiles: File[]) => {
    const acceptedFile = acceptedFiles[0];
    const fileSizeMB = acceptedFile.size / (1024 * 1024);

    const isValidFileType =
      acceptedFile.type.startsWith('image') ||
      acceptedFile.type.startsWith('video');
    const isValidSize = fileSizeMB <= 15;

    if (!isValidFileType || !isValidSize) {
      setFile(null);
      setErrorMessage(
        'File contains an unknown or unsupported file type, or exceeds the size limit of 15MB',
      );
    } else {
      setFile(acceptedFile);
      setErrorMessage(null);
    }
  };

  const handleSubmit = () => {
    if (!message || !bugType) {
      setErrorMessage('Both issue type and description are required');
      return;
    }

    setErrorMessage(null);

    const payload: any = {
      bug_type: bugType?.value,
      description: message,
      communication_type: communicationType,
    };

    if (file) {
      payload.file = file;
    }

    submitSupportTicket(payload);

    setMessage('');
    setFile(null);
    setBugType(null);
    setCommunicationType('');
  };

  const renderSuccess = () => (
    <View style={styles.successContainer}>
      <Ionicons
        name="checkmark-circle"
        size={100}
        color={CCColors?.Green}
        style={styles.successIcon}
      />
      <Spacer spacing={vs(10)} />
      <CCText style={styles.modalTitle}>We have received your query</CCText>
      <Spacer spacing={vs(10)} />
      <CCText style={styles.successText}>
        Thank you for submitting your issue. Your ticket ID is {ticketId}. Our
        team will reach out to you before the mentioned SLA time{' '}
        {commDate
          ? `${formatDateToDDMMYYYY(commDate)} ${formatTimeHHMM(commDate)}`
          : '--'}
        .
      </CCText>
      <Spacer spacing={vs(20)} />
      <View style={styles.infoRow}>
        <View style={styles.infoBox}>
          <CCText fontSize={20} color={CCColors.Primary.Brown}>
            #
          </CCText>
          <View style={styles.verticalLine} />
          <View>
            <CCText
              fontSize={FontSizes[12]}
              color={CCColors?.TextColor?.LightGrey}>
              Ticket ID
            </CCText>
            <CCText
              fontSize={FontSizes[12]}
              color={CCColors?.TextColor?.Primary}>
              {ticketId}
            </CCText>
          </View>
        </View>
        <View style={styles.infoBox}>
          <Ionicons
            name="time-outline"
            size={20}
            color={CCColors.Primary.Brown}
          />
          <View style={styles.verticalLine} />
          <View>
            <CCText
              fontSize={FontSizes[12]}
              color={CCColors?.TextColor?.LightGrey}>
              Ticket SLA
            </CCText>
            <CCText
              fontSize={FontSizes[12]}
              color={CCColors?.TextColor?.Primary}>
              {formatDateToDDMMYYYY(commDate)} | {formatTimeHHMM(commDate)}
            </CCText>
          </View>
        </View>
      </View>
      <Spacer spacing={vs(25)} />
      <View style={styles.buttonRow}>
        <CCButton
          onPress={() => navigation.navigate(ScreenName?.SupportTicket)}
          style={styles.viewTicketBtn}
          contentContainerStyle={{paddingVertical: 12, paddingHorizontal: 50}}
          titleStyle={{
            fontSize: FontSizes[16],
            color: CCColors?.TextColor?.Dark,
          }}>
          View Ticket
        </CCButton>
        <CCButton
          onPress={() => navigation.navigate(ScreenName?.Dashboard)}
          style={styles.okayBtn}
          contentContainerStyle={{paddingVertical: 12, paddingHorizontal: 50}}
          titleStyle={{
            fontSize: FontSizes[16],
            color: CCColors?.TextColor?.Dark,
          }}>
          Okay
        </CCButton>
      </View>
      <Spacer spacing={vs(20)} />
    </View>
  );

  return (
    <ModalContent
      success={success}
      renderSuccess={renderSuccess}
      message={message}
      setMessage={setMessage}
      bugType={bugType}
      setBugType={setBugType}
      communicationType={communicationType}
      setCommunicationType={setCommunicationType}
      file={file}
      handleDrop={handleDrop}
      errorMessage={errorMessage}
      setFile={setFile}
      isImage={isImage}
      handleSubmit={handleSubmit}
      issueCategories={issueCategories || []}
      isLoading={isLoading}
    />
  );
};

export default ReportBug;
